import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { Input, Textarea, Card, CardBody, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Button, Select, Spinner, Alert } from '@components/ui';
import { DocumentDuplicateIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, XMarkIcon, CheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { maskApiKey } from '@lib/utils/apiKeyMask';
import { cn } from '@lib/utils/cn';
import { useCompanyAccess } from '../hooks/useDevTools';
import { useUpdateCompanySettings } from '@features/company/hooks/useCompany';
import { useApiKeys, useDeleteApiKey } from '@features/apikeys/hooks/useApiKeys';
import { GenerateApiKeyModal } from '@features/apikeys/components/GenerateApiKeyModal';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const FALLBACK_VALUE = 'Sin data';

async function copyToClipboard(value: string) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    Swal.fire({
      icon: 'success',
      title: 'Copiado al portapapeles',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1200,
    });
  } catch {
    /* clipboard puede fallar si el navegador no tiene permiso — se ignora silenciosamente */
  }
}

function ReadOnlyField({
  label,
  value,
  multiline = false,
  testId,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  testId?: string;
}) {
  const handleClick = (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.select();
    copyToClipboard(value);
  };

  if (multiline) {
    return (
      <Textarea
        label={label}
        value={value}
        readOnly
        rows={3}
        onClick={handleClick}
        className="cursor-pointer font-mono text-xs"
        data-testid={testId}
      />
    );
  }

  return (
    <Input
      label={label}
      value={value}
      readOnly
      onClick={handleClick}
      className="cursor-pointer"
      data-testid={testId}
    />
  );
}

/**
 * Campo para secretos reales (ApimSubscriptionKey, BlueprintSign, BlueprintAuth, JWT).
 * A diferencia de ReadOnlyField: oculto por defecto (enmascarado con maskApiKey,
 * mismo criterio que la tabla de API Keys de abajo), y copiar/revelar requiere un
 * clic explícito en su propio botón — nunca con solo tocar el campo. Así una
 * captura de pantalla o un compartir de pantalla no expone el valor por accidente,
 * y el valor real no queda seleccionable en el DOM salvo que se revele a propósito.
 */
function SecretField({
  label,
  value,
  multiline = false,
  testId,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  testId?: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const isFallback = value === FALLBACK_VALUE || !value;
  const displayValue = isFallback ? value : revealed ? value : maskApiKey(value);

  // Copiar solo tiene sentido (y solo se habilita) con el valor completo a la
  // vista — evita copiar a ciegas un valor todavía enmascarado.
  const actions = !isFallback && (
    <div
      className={cn(
        'absolute right-0 flex gap-0.5 pr-1.5',
        multiline ? 'top-0 pt-1.5' : 'inset-y-0 items-center'
      )}
    >
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        className="p-1.5 rounded text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200"
        title={revealed ? 'Ocultar valor' : 'Revelar valor'}
        data-testid={testId ? `${testId}-toggle-reveal` : undefined}
      >
        {revealed ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
      </button>
      <button
        type="button"
        onClick={() => copyToClipboard(value)}
        disabled={!revealed}
        className="p-1.5 rounded text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 disabled:opacity-30 disabled:pointer-events-none"
        title={revealed ? 'Copiar valor' : 'Revela el valor para poder copiarlo'}
        data-testid={testId ? `${testId}-copy-button` : undefined}
      >
        <DocumentDuplicateIcon className="w-4 h-4" />
      </button>
    </div>
  );

  // El valor enmascarado es una fila de asteriscos y la etiqueta trae palabras
  // como "Auth"/"Key"/"JWT" — justo lo que el autocompletado/gestor de
  // contraseñas del navegador usa para clasificar un campo como contraseña y
  // ofrecer guardarla. Estos atributos apagan esa detección en Chrome/Edge/
  // Firefox y en los gestores de terceros más comunes (LastPass, 1Password,
  // Bitwarden, Dashlane) — el campo es de solo lectura, no hay nada que
  // "guardar" como credencial de sitio.
  const noPasswordManagerProps = {
    autoComplete: 'off',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
  } as const;

  if (multiline) {
    return (
      <div>
        <span className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">{label}</span>
        <div className="relative">
          <Textarea
            value={displayValue}
            readOnly
            rows={3}
            className="font-mono text-xs pr-16"
            data-testid={testId}
            {...noPasswordManagerProps}
          />
          {actions}
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">{label}</span>
      <div className="relative">
        <Input
          value={displayValue}
          readOnly
          className="font-mono text-xs pr-16"
          data-testid={testId}
          {...noPasswordManagerProps}
        />
        {actions}
      </div>
    </div>
  );
}

function EditableCompanyCodeField({ initialValue, testId }: { initialValue: string; testId?: string }) {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateCompanySettings();
  const [value, setValue] = useState(initialValue);

  const trimmed = value.trim();
  const isFallback = initialValue === FALLBACK_VALUE;
  const isUnchanged = trimmed === initialValue.trim();
  const isEmpty = trimmed.length === 0;
  const canSave = !isFallback && !isUnchanged && !isEmpty && !updateMutation.isPending;

  const handleSave = async () => {
    if (!canSave) return;

    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Actualizar el Company Code?',
      html: `El código de la compañía cambiará de <b>${initialValue}</b> a <b>${trimmed}</b>.<br/>Es un identificador de la empresa; asegúrate de que el valor sea correcto.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    try {
      await updateMutation.mutateAsync({ code: trimmed });
      queryClient.invalidateQueries({ queryKey: ['devtools', 'company-access'] });
      Swal.fire({
        icon: 'success',
        title: 'Company Code actualizado',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar el Company Code',
        text: error?.response?.data?.message || 'Ocurrió un error de red o del servidor. Intenta nuevamente.',
      });
    }
  };

  return (
    <div>
      <Input
        label="Company Code"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={isFallback || updateMutation.isPending}
        helperText={isFallback ? 'No hay datos de la compañía para editar.' : undefined}
        data-testid={testId}
      />
      <div className="mt-2 flex justify-end">
        <Button
          variant="primary"
          size="sm"
          leftIcon={<CheckIcon className="w-4 h-4" />}
          onClick={handleSave}
          disabled={!canSave}
          isLoading={updateMutation.isPending}
          data-testid="devtools-company-info-tab-company-code-save-button"
        >
          Guardar
        </Button>
      </div>
    </div>
  );
}

function ApiKeysSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const { data: apiKeys = [], isLoading, isError } = useApiKeys();
  const deleteMutation = useDeleteApiKey();

  const filteredApiKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return apiKeys;
    return apiKeys.filter(
      (key) =>
        (key.name || '').toLowerCase().includes(term) ||
        (key.apiKey || '').toLowerCase().includes(term)
    );
  }, [apiKeys, search]);

  const totalPages = Math.max(1, Math.ceil(filteredApiKeys.length / pageSize));

  const paginatedApiKeys = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredApiKeys.slice(start, start + pageSize);
  }, [filteredApiKeys, page, pageSize]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleDelete = async (id: string, name: string) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Está seguro?',
      text: `Esta acción no se puede revertir! Se eliminará el Api Key "${name}".`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });

    if (!confirm.isConfirmed) return;

    try {
      const result = await deleteMutation.mutateAsync(id);
      if (result.success) {
        Swal.fire({ icon: 'success', title: 'Api Key eliminado', timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'No se pudo eliminar el Api Key',
          text: result.message || 'Ocurrió un error inesperado.',
        });
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo eliminar el Api Key',
        text: 'Ocurrió un error de red o del servidor. Intenta nuevamente.',
      });
    }
  };

  return (
    <Card>
      <CardBody>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
            API Keys de la compañía
          </h3>
          <Button variant="primary" size="sm" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setIsModalOpen(true)} data-testid="devtools-company-info-tab-generate-api-key-button">
            Generar Nuevo Api Key
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12" data-testid="devtools-company-info-tab-api-keys-loading-spinner">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <Alert variant="danger" title="No se pudieron cargar las API Keys" data-testid="devtools-company-info-tab-api-keys-error-alert">
            Ocurrió un error consultando las API Keys. Intenta recargar la página.
          </Alert>
        ) : apiKeys.length === 0 ? (
          <p className="text-sm text-secondary-500 dark:text-secondary-400 py-6 text-center">
            No hay API Keys registradas.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="relative max-w-sm flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nombre o Api Key..."
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-10 py-2 rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  data-testid="devtools-company-info-tab-api-keys-search-input"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600"
                    data-testid="devtools-company-info-tab-api-keys-search-clear-button"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-secondary-500 dark:text-secondary-400 whitespace-nowrap">
                  Mostrar
                </span>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="w-auto py-1"
                  options={PAGE_SIZE_OPTIONS.map((size) => ({ value: String(size), label: String(size) }))}
                  data-testid="devtools-company-info-tab-api-keys-page-size-select"
                />
              </div>
            </div>

            {filteredApiKeys.length === 0 ? (
              <p className="text-sm text-secondary-500 dark:text-secondary-400 py-12 text-center">
                No se encontraron API Keys que coincidan con "{search}".
              </p>
            ) : (
              <>
                <Table data-testid="devtools-company-info-tab-api-keys-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Api Key</TableHead>
                      <TableHead className="w-48">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedApiKeys.map((key) => (
                      <TableRow key={key.id} data-testid={`devtools-company-info-tab-api-keys-row-${key.id}`}>
                        <TableCell>{key.name}</TableCell>
                        <TableCell>{key.description}</TableCell>
                        <TableCell className="font-mono text-xs">{maskApiKey(key.apiKey)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<DocumentDuplicateIcon className="w-4 h-4" />}
                              onClick={() => copyToClipboard(key.apiKey)}
                              data-testid={`devtools-company-info-tab-api-keys-copy-button-${key.id}`}
                            >
                              Copiar
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              leftIcon={<TrashIcon className="w-4 h-4" />}
                              onClick={() => handleDelete(key.id, key.name)}
                              data-testid={`devtools-company-info-tab-api-keys-delete-button-${key.id}`}
                            >
                              Eliminar
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="devtools-company-info-tab-api-keys-prev-page-button">
                      Anterior
                    </Button>
                    <span className="text-sm text-secondary-500 dark:text-secondary-400">
                      Página {page} de {totalPages} ({filteredApiKeys.length} en total)
                    </span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="devtools-company-info-tab-api-keys-next-page-button">
                      Siguiente
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardBody>

      <GenerateApiKeyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Card>
  );
}

export function CompanyInfoTab() {
  const { data, isLoading, isError } = useCompanyAccess();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12" data-testid="devtools-company-info-tab-loading-spinner">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert variant="danger" title="No se pudo cargar la información" data-testid="devtools-company-info-tab-error-alert">
        Ocurrió un error consultando los datos de la compañía. Intenta recargar la página.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardBody>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-4">
            Click en Company Name / Company Id para copiarlos al portapapeles. Los campos de credenciales
            (ApimSubscriptionKey, BlueprintSign, BlueprintAuth, JWT) están ocultos por defecto — usa el ícono
            del ojo para revelarlos y el de copiar para llevarlos al portapapeles.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ReadOnlyField label="Company Name" value={data.companyName} testId="devtools-company-info-tab-company-name-input" />
            <EditableCompanyCodeField key={data.companyCode} initialValue={data.companyCode} testId="devtools-company-info-tab-company-code-input" />
            <ReadOnlyField label="Company Id" value={data.companyId} testId="devtools-company-info-tab-company-id-input" />
            <SecretField label="ApimSubscriptionKey" value={data.apimSubscriptionKey} testId="devtools-company-info-tab-apim-subscription-key-input" />
            <SecretField label="BlueprintSign" value={data.blueprintSign} testId="devtools-company-info-tab-blueprint-sign-input" />
            <SecretField label="BlueprintAuth" value={data.blueprintAuth} testId="devtools-company-info-tab-blueprint-auth-input" />
          </div>
          <div className="mt-4">
            <SecretField label="JWT" value={data.jwt} multiline testId="devtools-company-info-tab-jwt-textarea" />
          </div>
        </CardBody>
      </Card>

      <ApiKeysSection />
    </div>
  );
}
